<template>
  <v-dialog v-model="dialog" max-width="50%">
    <template v-slot:activator="{ on }">
      <v-btn color="primary" dark v-on="on" v-if="!isEdit">
        <v-icon left>fas fa-dolly</v-icon>
        New Shipment
      </v-btn>
      <v-btn v-else icon color="primary" v-on="on">
        <v-icon>edit</v-icon>
      </v-btn>
    </template>
    <v-card>
      <v-card-title>Shippment Form</v-card-title>
      <v-card-text>
        <v-form v-model="$v.valid" @submit.prevent="submit" class="pa-5">
          <v-text-field
            label="Ticket Name"
            :error-messages="nameErrors"
            counter="48"
            v-model="$v.formdata.name.$model"
            @input="$v.formdata.name.$touch()"
            @blur="$v.formdata.name.$touch()"
          />
          <v-text-field
            label="Shipment Date"
            :error-messages="shipdateErrors"
            counter="128"
            v-model="$v.formdata.ship_date.$model"
            @input="$v.formdata.ship_date.$touch()"
            @blur="$v.formdata.ship_date.$touch()"
          />
          <v-text-field
            label="Tracking Number"
            :error-messages="trackingErrors"
            counter="64"
            v-model="$v.formdata.tracking.$model"
            @input="$v.formdata.tracking.$touch()"
            @blur="$v.formdata.tracking.$touch()"
          />
          <v-text-field
            label="Phone Number"
            :error-messages="phoneErrors"
            counter="64"
            v-model="$v.formdata.phone.$model"
            @input="$v.formdata.phone.$touch()"
            @blur="$v.formdata.phone.$touch()"
          />
          <v-text-field
            label="Shipping Attention"
            :error-messages="shiptoErrors"
            hint="ATTN of shipper"
            counter="128"
            v-model="$v.formdata.shipto.$model"
            @input="$v.formdata.shipto.$touch()"
            @blur="$v.formdata.shipto.$touch()"
          />
          <v-textarea
            label="Shipping Address"
            v-model="formdata.address"
          />
          <v-text-field
            label="Notes"
            placeholder="Expedited, overnight, ground, etc..."
            hint="Indicate the type of shipping or unique packaging"
            :error-messages="notesErrors"
            counter="128"
            v-model="$v.formdata.notes.$model"
            @input="$v.formdata.notes.$touch()"
            @blur="$v.formdata.notes.$touch()"
          />
          <v-text-field
            label="Account Number"
            placeholder="FedEx #: ..."
            hint="Indicate what shipping account will be charged"
            :error-messages="accountErrors"
            counter="128"
            v-model="$v.formdata.account_number.$model"
            @input="$v.formdata.account_number.$touch()"
            @blur="$v.formdata.account_number.$touch()"
          />
          <v-select
            label="Status"
            :items="states"
            :error-messages="stateErrors"
            v-model="$v.formdata.state.$model"
            @input="$v.formdata.state.$touch()"
            @blur="$v.formdata.state.$touch()"
          />
          <v-autocomplete
            v-model="$v.formdata.requester_id.$model"
            :error-messages="userErrors"
            :loading="userLoading"
            label="Shipping Requester"
            :items="users"
            item-value="id"
            item-text="display_name"
            hint="Who should the shipper contact in case of questions"
            prepend-icon="fas fa-users"
            @input="$v.formdata.requester_id.$touch()"
            @blur="$v.formdata.requester_id.$touch()"
          />
          <v-switch
            label="Customer Pays Shipping"
            hint="Check here if the customer should pay for shipping costs"
            v-model="formdata.customer"
          >
          </v-switch>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="error" dark @click="dialog = false">
          <v-icon left>mdi-close</v-icon>
          Close
        </v-btn>
        &nbsp;
        <v-btn color="warning" dark @click="clear">
          <v-icon left>fas fa-redo</v-icon>
          Clear
        </v-btn>
        &nbsp;
        <v-btn :disabled="$v.$invalid" color="primary" @click="submit">
          <v-icon dark left>
            fas fa-save
          </v-icon>
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { Shipping } from "../../models/Shipping";
import { handleErrors } from "../../services/formErrors";
import { validationMixin } from "vuelidate";
import User from "../../models/User";
export default {
  name: "ShippingForm",
  mixins: [validationMixin],
  validations: Shipping.validations,
  data() {
    return {
      dialog: false,
      formdata: new Shipping(this.instance),
      states: ["Requested", "Shipped", "Archived"]
    };
  },
  props: {
    isEdit: {
      type: Boolean,
      required: true,
      default: false
    },
    instance: {
      type: Object,
      default: () => {}
    },
    userLoading: {
      type: Boolean,
      required: true,
      default: true
    }
  },
  methods: {
    submit() {
      this.$v.$touch();
      if (this.isEdit) {
        Shipping
          .$put(this.instance.id, this.formdata.toUpdatePayload())
          .then(() => this.dialog = false)
      } else {
        this.formdata.rawmaterial_id = this.rawmaterialid;
        Shipping
          .$post(this.formdata.toCreatePayload())
          .then(() => this.clear())
      }
    },
    clear() {
      this.formdata = new Shipping();
      this.$v.$reset();
      this.dialog = false;
    }
  },
  computed: {
    users() {
      return User.query().orderBy('display_name').get();
    },
    userErrors() {
      return handleErrors("formdata", "requester_id", this);
    },
    nameErrors() {
      return handleErrors("formdata", "name", this);
    },
    shipdateErrors() {
      return handleErrors("formdata", "ship_date", this);
    },
    trackingErrors() {
      return handleErrors("formdata", "tracking", this);
    },
    phoneErrors() {
      return handleErrors("formdata", "phone", this);
    },
    shiptoErrors() {
      return handleErrors("formdata", "shipto", this);
    },
    stateErrors() {
      return handleErrors("formdata", "state", this);
    },
    notesErrors() {
      return handleErrors("formdata", "notes", this);
    },
    accountErrors() {
      return handleErrors("formdata", "account_number", this);
    }
  }
};
</script>
